import { Component, Input, NgModule, Output } from '@angular/core';
import { PaginationSet } from '@model/paginationset/paginationset';
import { Subject } from 'rxjs';
import { SharedModule } from 'src/app/shared.module';

@Component({
  selector: 'app-pagination',
  templateUrl: './pagination.component.html',
  styleUrls: ['./pagination.component.scss'],
})
export class PaginationComponent {
  @Input() public stateParams: any;

  @Output() public onGotoPage: Subject<any> = new Subject();

  @Input()
  public set pagination(pagination: PaginationSet<any>) {
    if (pagination) {
      this._pagination = pagination;
      this.refreshPagination();
    }
  }
  public get pagination(): PaginationSet<any> {
    return this._pagination;
  }
  private _pagination: PaginationSet<any>;

  public countPages = 0;
  public currentIndex = 0;
  public buttons: number[] = [];
  private isResetting = false; // To prevent loops

  /**
   * Navigate to a specific page.
   * Ensures the page is valid before triggering navigation.
   */
  public goToPage(page: number): void {
    if (this.isPageValid(page)) {
      this.onGotoPage.next({ filter: 'pagination', value: page });
      window.scrollTo(0, 0);
    }
  }

  /**
   * Refresh the pagination state: calculate total pages, validate the current page,
   * and update the button display.
   */
  private refreshPagination(): void {
    this.calculatePageCount();
    this.validateCurrentPage();
    this.generateButtons();
  }

  /**
   * Calculates the total number of pages based on the `pagination_size` and total count.
   */
  private calculatePageCount(): void {
    this.countPages = Math.ceil(this._pagination.count / this._pagination.pagination_size);
  }

  /**
   * Validates the current page. If invalid, resets it to the first page.
   */
  private validateCurrentPage(): void {
    const requestedPage = parseInt(this.stateParams.pagination || '0', 10);

    // If the requested page is out of range, reset to the first page
    if (!this.isPageValid(requestedPage)) {
      console.log('requested page ====');
      console.log(requestedPage);
      this.resetToFirstPage();
    } else {
      this.currentIndex = requestedPage;
    }
  }

  /**
   * Determines if the given page number is within a valid range.
   */
  private isPageValid(page: number): boolean {
    return page >= 0 && page < this.countPages;
  }

  /**
   * Resets the pagination to the first page and emits the corresponding event.
   */
  private resetToFirstPage(): void {
    if (this.isResetting) return; // Prevent loops during resets

    console.log('Invalid page number, resetting to the first page.');
    this.isResetting = true;
    this.currentIndex = 0;
    this.onGotoPage.next({ filter: 'pagination', value: 0 });

    // setTimeout(() => (this.isResetting = false), 100); // Small delay to prevent recursive triggers
  }

  /**
   * Generates pagination buttons for the UI based on the current page and total pages.
   */
  private generateButtons(): void {
    this.buttons = [];

    if (this.countPages <= 6) {
      // Show all page numbers if total pages are <= 6
      for (let index = 0; index < this.countPages; index++) {
        this.buttons.push(index);
      }
    } else {
      // Ellipsis-style pagination for larger page sets
      this.buttons.push(0);

      if (this.currentIndex > 2) {
        this.buttons.push(-10); // Ellipsis placeholder
      }

      for (
        let index = Math.max(0, this.currentIndex - 2);
        index <= Math.min(this.countPages - 1, this.currentIndex + 2);
        index++
      ) {
        // make sure that only unique indecies are added to the buttons
        if (!this.buttons.includes(index)) {
          this.buttons.push(index);
        }
      }

      if (this.currentIndex < this.countPages - 3) {
        this.buttons.push(-10); // Ellipsis placeholder
      }

      this.buttons.push(this.countPages - 1);
    }

    // assign only unique items
    this.buttons = this.buttons.filter((value, index, array) => array.indexOf(value) == index);
  }
}

@NgModule({
  declarations: [PaginationComponent],
  imports: [SharedModule],
  exports: [PaginationComponent, SharedModule],
})
export class PaginationComponentModule {}
